/**
 * Minutas, CCG e assinaturas.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams } from './comum';
import type { DeliveryFormat, Template, TemplateFormat } from './templates';

import { api, requestPage } from '../client';

export type GeneratedKind = 'minuta' | 'ccg' | 'apolice' | 'laudo_emissao';
export type GeneratedStatus = 'vigente' | 'substituida' | 'cancelada';

export type SignatureFlowStatus = 'em_coleta' | 'concluido' | 'cancelado';
export type SignerRole = 'representante_legal' | 'avalista' | 'corretor' | 'testemunha';
export type SignerStatus = 'pendente' | 'assinado' | 'recusado';

export interface SignatureFlow {
  id: string;
  number: number;
  document_id: string;
  /** Hoje existe um unico valor: o registro interno de assinatura colhida fora. */
  provider: 'registro_interno';
  status: SignatureFlowStatus;
  notes: string;
  opened_label: string;
  closed_at: string | null;
  created_at: string;
}

export interface GeneratedDocument {
  id: string;
  number: number;
  operation_id: string;
  kind: GeneratedKind;
  template_id: string;
  template_version: number;
  /** Texto, quando o modelo e de texto; docx, quando o documento tem arquivo. */
  format: TemplateFormat;
  body: string;
  status: GeneratedStatus;
  /** Arquivo em Word. Vazio no documento de texto. */
  filename: string;
  byte_size: number;
  /** O PDF ja foi gerado e esta guardado. */
  pdf_ready: boolean;
  /** O PDF pode ser baixado agora: ja existe, ou ha conversor nesta instalacao. */
  pdf_available: boolean;
  pdf_byte_size: number;
  generated_label: string;
  generated_at: string;
  signature_flow: SignatureFlow | null;
}

/** Endereco temporario do documento gerado, com o nome que chega ao navegador. */
export interface GeneratedDocumentFile {
  url: string;
  filename: string;
  format: DeliveryFormat;
}

export interface Signer {
  id: string;
  flow_id: string;
  position: number;
  name: string;
  email: string;
  role: SignerRole;
  status: SignerStatus;
  recorded_label: string;
  signed_at: string | null;
  note: string;
}

export interface SignatureFlowDetail extends SignatureFlow {
  document: GeneratedDocument;
  signers: Signer[];
}

export interface SignerInput {
  name: string;
  email?: string;
  role: SignerRole;
}

export const generated = {
  listDrafts: (params: ListParams, signal?: AbortSignal) =>
    requestPage<GeneratedDocument>(`/api/v1/drafts${query(params)}`, signal),
  listCounterGuarantees: (params: ListParams, signal?: AbortSignal) =>
    requestPage<GeneratedDocument>(
      `/api/v1/counter-guarantees${query(params)}`,
      signal,
    ),
  get: (id: string, signal?: AbortSignal) =>
    api.get<GeneratedDocument>(`/api/v1/generated-documents/${id}`, signal),
  generateDraft: (operationId: string, templateId: string) =>
    api.post<GeneratedDocument>(`/api/v1/operations/${operationId}/drafts`, {
      template_id: templateId,
    }),
  /** Minuta da cotacao vinculada a modalidade da operacao (P-MIN-5). */
  boundDraftTemplate: (operationId: string, signal?: AbortSignal) =>
    api.get<Template>(`/api/v1/operations/${operationId}/draft-template`, signal),
  generateCounterGuarantee: (operationId: string, templateId: string) =>
    api.post<GeneratedDocument>(
      `/api/v1/operations/${operationId}/counter-guarantees`,
      { template_id: templateId },
    ),
  cancel: (id: string) =>
    api.post<GeneratedDocument>(`/api/v1/generated-documents/${id}/cancellation`, {}),

  /**
   * Endereco temporario do arquivo gerado. O PDF nasce no primeiro pedido e fica
   * guardado ao lado do Word.
   */
  download: (id: string, format: DeliveryFormat = 'docx') =>
    api.get<GeneratedDocumentFile>(
      `/api/v1/generated-documents/${id}/download?format=${format}`,
    ),
};

/**
 * A contagem por situacao vem em `meta`, calculada no banco.
 */
export async function listSignatureFlows(
  params: ListParams,
  signal?: AbortSignal,
): Promise<{
  items: SignatureFlow[];
  total: number;
  limit: number;
  offset: number;
  byStatus: Record<string, number>;
}> {
  const page = await requestPage<SignatureFlow>(
    `/api/v1/signature-flows${query(params)}`,
    signal,
  );
  const byStatus = (page.meta.by_status as Record<string, number> | undefined) ?? {};
  return { ...page, byStatus };
}

export const signatures = {
  list: listSignatureFlows,
  get: (id: string, signal?: AbortSignal) =>
    api.get<SignatureFlowDetail>(`/api/v1/signature-flows/${id}`, signal),
  open: (documentId: string, notes: string, signers: SignerInput[]) =>
    api.post<SignatureFlowDetail>(
      `/api/v1/generated-documents/${documentId}/signature-flow`,
      { notes, signers },
    ),
  record: (flowId: string, signerId: string, signed: boolean, note: string) =>
    api.post<SignatureFlowDetail>(
      `/api/v1/signature-flows/${flowId}/signers/${signerId}/record`,
      { signed, note },
    ),
  complete: (flowId: string) =>
    api.post<SignatureFlowDetail>(`/api/v1/signature-flows/${flowId}/completion`, {}),
  cancel: (flowId: string) =>
    api.post<SignatureFlowDetail>(`/api/v1/signature-flows/${flowId}/cancellation`, {}),
};
